import { useEffect, useRef, useState } from "react";
import { Api } from "services/api/api.service";
import type {
  EditTimeReportData,
  TimeReportsV2Item,
} from "services/api/company/timeReports.service";
import {
  createInlineTimeReportDraft,
  hasInlineTimeReportChanges,
  hasInlineTimeReportDraftChanges,
  resolveInlineTimeReportPayload,
} from "web-apps/company/components/timeReportsV2/components/views/person/models/personTimeReport.inlineEdit.logic";
import type {
  InlineTimeReportDraft,
  InlineTimeReportError,
} from "web-apps/company/components/timeReportsV2/components/views/person/models/personTimeReport.inlineEdit.logic";
import { canEditTimeReport } from "web-apps/company/components/timeReportsV2/components/views/person/models/timeReportActionPolicy";

interface UseInlineTimeReportEditParams {
  readonly companyId?: number;
  readonly isReadOnly: boolean;
  readonly item: TimeReportsV2Item;
  readonly onPendingChange?: (hasPendingChanges: boolean) => void;
  readonly onSaved: () => void;
}

export interface InlineTimeReportEditActivity {
  readonly hasPendingChanges: boolean;
  readonly timeReportId: number;
}

type InlineSaveState =
  | { readonly type: "idle" }
  | { readonly type: "saving" }
  | { readonly type: "error" };

interface InlineTimeReportFieldChange {
  readonly field: "start" | "end";
  readonly value: string;
}

interface InlineTimeReportEditState {
  readonly draft: InlineTimeReportDraft;
  readonly hasSaveError: boolean;
  readonly isDisabled: boolean;
  readonly isSaving: boolean;
  readonly validationError: InlineTimeReportError | null;
  readonly save: () => Promise<void>;
  readonly setField: (input: InlineTimeReportFieldChange) => void;
  readonly toggleBreak: (isEnabled: boolean) => void;
}

export const useInlineTimeReportEdit = ({
  companyId,
  isReadOnly,
  item,
  onSaved,
  onPendingChange,
}: UseInlineTimeReportEditParams): InlineTimeReportEditState => {
  const [draft, setDraft] = useState(() => createInlineTimeReportDraft(item));
  const [saveState, setSaveState] = useState<InlineSaveState>({ type: "idle" });
  const [validationError, setValidationError] =
    useState<InlineTimeReportError | null>(null);
  const draftRef = useRef(draft);
  const baselineRef = useRef(draft);
  const savedPayloadRef = useRef<EditTimeReportData | null>(null);
  const isSavingRef = useRef(false);
  const hasRequestedSaveRef = useRef(false);
  const isMountedRef = useRef(true);
  const paramsRef = useRef({
    companyId,
    isReadOnly,
    item,
    onSaved,
    onPendingChange,
  });
  paramsRef.current = { companyId, isReadOnly, item, onSaved, onPendingChange };
  const isDisabled =
    isReadOnly || !companyId || !canEditTimeReport(item.status);

  const notifyPendingChanges = (): void => {
    paramsRef.current.onPendingChange?.(
      isMountedRef.current &&
        (isSavingRef.current ||
          hasInlineTimeReportDraftChanges({
            baseline: baselineRef.current,
            draft: draftRef.current,
          })),
    );
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (!isSavingRef.current) paramsRef.current.onPendingChange?.(false);
    };
  }, []);

  useEffect(() => {
    if (isSavingRef.current) return;
    const persisted = savedPayloadRef.current;
    // An earlier refresh can finish after a later save; wait for acknowledgement
    // before replacing the local baseline with server values.
    if (
      persisted &&
      hasInlineTimeReportChanges({
        item: paramsRef.current.item,
        payload: persisted,
      })
    )
      return;
    const incoming = createInlineTimeReportDraft(paramsRef.current.item);
    if (!isSavingRef.current) savedPayloadRef.current = null;
    const current = draftRef.current;
    const baseline = baselineRef.current;
    const next = {
      start: current.start === baseline.start ? incoming.start : current.start,
      end: current.end === baseline.end ? incoming.end : current.end,
      breakMinutes:
        !incoming.breakEnabled && !current.breakEnabled
          ? current.breakMinutes
          : current.breakMinutes === baseline.breakMinutes
            ? incoming.breakMinutes
            : current.breakMinutes,
      breakEnabled:
        current.breakEnabled === baseline.breakEnabled
          ? incoming.breakEnabled
          : current.breakEnabled,
    };
    baselineRef.current = incoming;
    draftRef.current = next;
    setDraft(next);
    notifyPendingChanges();
  }, [
    item.reportedStartTime,
    item.reportedEndTime,
    item.breakStartTime,
    item.breakEndTime,
    item.updatedAt,
    saveState.type,
  ]);

  const save = async (): Promise<void> => {
    if (isDisabled) return;
    hasRequestedSaveRef.current = true;
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    notifyPendingChanges();
    try {
      while (hasRequestedSaveRef.current) {
        hasRequestedSaveRef.current = false;
        const currentParams = paramsRef.current;
        if (
          !currentParams.companyId ||
          currentParams.isReadOnly ||
          !canEditTimeReport(currentParams.item.status)
        )
          break;
        const snapshot = { ...draftRef.current };
        const baseline = baselineRef.current;
        // Compare displayed values before rebuilding timestamps, which can
        // normalize seconds or supply missing break timestamps.
        if (!hasInlineTimeReportDraftChanges({ baseline, draft: snapshot })) {
          if (isMountedRef.current) {
            setValidationError(null);
            setSaveState((current) =>
              current.type === "error" ? { type: "idle" } : current,
            );
          }
          continue;
        }
        const persisted = savedPayloadRef.current;
        const comparisonItem = persisted
          ? {
              ...currentParams.item,
              reportedStartTime: persisted.startTime,
              reportedEndTime: persisted.endTime,
              breakStartTime: persisted.breakStartTime,
              breakEndTime: persisted.breakEndTime,
            }
          : currentParams.item;
        const result = resolveInlineTimeReportPayload({
          item: comparisonItem,
          draft: snapshot,
        });
        if (!result.ok) {
          if (isMountedRef.current) setValidationError(result.error);
          break;
        }
        if (isMountedRef.current) setValidationError(null);
        if (
          !hasInlineTimeReportChanges({
            item: comparisonItem,
            payload: result.value,
          })
        )
          continue;
        if (isMountedRef.current) {
          setSaveState({ type: "saving" });
        }
        await Api().company.timeReports.editTimeReport(
          currentParams.companyId,
          currentParams.item.id,
          result.value,
        );
        savedPayloadRef.current = result.value;
        baselineRef.current = snapshot;
        currentParams.onSaved();
        notifyPendingChanges();
      }
    } catch {
      hasRequestedSaveRef.current = false;
      if (isMountedRef.current) setSaveState({ type: "error" });
    } finally {
      isSavingRef.current = false;
      notifyPendingChanges();
      if (isMountedRef.current)
        setSaveState((current) =>
          current.type === "saving" ? { type: "idle" } : current,
        );
    }
  };

  const updateDraft = (next: InlineTimeReportDraft): void => {
    draftRef.current = next;
    setDraft(next);
    notifyPendingChanges();
    setValidationError(null);
    setSaveState((current) =>
      current.type === "error" ? { type: "idle" } : current,
    );
  };

  return {
    draft,
    isDisabled,
    isSaving: saveState.type === "saving",
    hasSaveError: saveState.type === "error",
    validationError,
    setField: ({ field, value }): void =>
      updateDraft({ ...draftRef.current, [field]: value }),
    toggleBreak: (isEnabled): void => {
      if (
        isDisabled ||
        (isEnabled && Number(draftRef.current.breakMinutes) <= 0)
      )
        return;
      updateDraft({ ...draftRef.current, breakEnabled: isEnabled });
      void save();
    },
    save,
  };
};
